// @flow
import React, { useEffect, useState } from 'react';
import { filter, isEmpty } from 'lodash';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { NavigationUtil } from 'utils/NavigationUtil';
import { RouteNames } from 'router';
import { HomeActions } from 'modules/home/actions';
import { HomeSelector } from 'modules/home/selector';
import { AwardSelector } from 'modules/awards/selector';
import { CmsPagesSelector } from 'modules/cmsPages/selector';
import { TestimonialSelector } from 'modules/testimonials/selector';
import { ProjectSelector } from 'modules/project/selector';
import { CmsDetailsSelector } from 'modules/cmsDetails/selector';
import { ProductBrowserSelector } from 'modules/productBrowser/selector';
import { ProjectActions } from 'modules/project/actions';
import { CmsPagesActions } from 'modules/cmsPages/actions';
import { AwardActions } from 'modules/awards/action';
import { CmsDetailsActions } from 'modules/cmsDetails/actions';
import { PageLoader } from 'components/molecules/pageLoader';
import WorkspaceBanner from 'components/organisms/workspaceBanner';
import { ContactUsSection } from 'components/templates/contactUsSection';
import FooterSection from 'components/templates/footer';
import PageTemplate from 'components/templates/pageTemplate';
import { useViewport } from 'components/templates/viewportProvider';
import type { AppState } from 'modules/rootReducer';
import type { Contact, SiteSettings, Vertical } from 'types/common';
import RenderSEO from 'components/atoms/renderSEO';
import OurHomesDifferences from 'components/templates/ourHomesDifference';
import { ProjectAnnouncementsSection } from 'components/templates/projectAnnouncementsSection';
import CompletedProjectsSection from 'components/templates/completedProjectsSection';
import Award from 'components/templates/awards';
import { GetInTouchModal } from 'components/templates/getInTouchModal';
import AllProperties from 'components/templates/allProperties';
import { ProjectBrowserSelector } from 'modules/projectBrowser/selector';
import { HeaderButtons } from 'components/atoms/headerButtons';
import { ShareModal } from 'components/templates/shareModal';
import Testimonial from 'components/templates/testimonial';
import { VideoActions } from 'modules/videos/action';
import { VideosSelector } from 'modules/videos/selector';
import { FooterActions } from 'modules/footerLink/actions';
import { FooterSelector } from 'modules/footerLink/selector';

type Props = {
  isLoading: boolean,
  verticals: Array<Vertical>,
  verticalId: number | null,
  getVerticals: () => void,
  siteSettings: SiteSettings,
  getSiteSettings: () => void,
  contact: Contact,
  getContactDetails: () => void,
};

const WorkspacesPage = (props: Props) => {
  const {
    isLoading,
    isCmsPageLoading,
    getAnnouncements,
    announcements,
    getVerticals,
    verticals,
    verticalId,
    getSiteSettings,
    siteSettings,
    getPageData,
    ourDifferenceData,
    getBannerData,
    getContactDetails,
    contact,
    bannerData,
    awardData,
    getAwardData,
    footerLink,
    getFooterLinkData,
    cmsDetailsData,
    getCmsDetailsData,
    getCompletedProjects,
    completedProjects,
    getProjects,
    allProjectsRecommendedList,
    projectList,
    getVideosData,
    videosData,
  } = props;

  const { isMobile } = useViewport();
  let [theme, setTheme] = useState(isMobile ? 'dark-theme' : 'light-theme');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  useEffect(() => {
    getContactDetails();
    getVerticals();
    getSiteSettings();
    getBannerData();
    getAnnouncements(verticalId);
    getPageData();
    getAwardData(verticalId);
    getCmsDetailsData();
    getCompletedProjects(verticalId);
    getProjects(verticalId);
    getFooterLinkData();
    getVideosData(verticalId);
  }, []);

  if (isLoading || isCmsPageLoading) {
    return <PageLoader isMobile={isMobile} />;
  }

  if (
    !contact ||
    !siteSettings ||
    !verticals ||
    !bannerData ||
    !announcements ||
    !ourDifferenceData ||
    !awardData ||
    !cmsDetailsData ||
    !videosData ||
    !footerLink
  ) {
    return null;
  }

  const selectedVertical = filter(verticals, ({ id }) => id === verticalId);
  const metaTagSEO =
    selectedVertical.length > 0 ? selectedVertical[0].metaTagSEO : '';

  // Get in touch
  const onGetInTouchClick = () => {
    setIsModalOpen(true);
  };

  const closeShareModal = () => {
    setIsShareModalOpen(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const CampaignCode = contact.campaignCode ? contact.campaignCode : '';

  const componentList = [
    ...(!isEmpty(bannerData)
      ? [
          {
            component: WorkspaceBanner,
            data: bannerData,
            theme: isMobile ? 'dark-theme' : 'light-theme',
            sectionId: 'banner',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Workcations',
                breadcrumbLink: RouteNames.workcations,
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(ourDifferenceData)
      ? [
          {
            component: OurHomesDifferences,
            data: {
              ourDifferenceData,
              cmsDetailsData,
            },
            sectionId: 'our-differences',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Workcations',
                breadcrumbLink: RouteNames.workcations,
              },
              {
                breadcrumbName: 'Our Differences',
                breadcrumbLink: '#',
              },
            ],
            theme: 'light-theme',
          },
        ]
      : []),
    ...(!isEmpty(allProjectsRecommendedList)
      ? [
          {
            component: AllProperties,
            sectionId: 'properties',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Workcations',
                breadcrumbLink: RouteNames.workcations,
              },
              {
                breadcrumbName: 'Our Projects',
                breadcrumbLink: '#',
              },
            ],
            theme: 'dark-theme',
            data: {
              data: allProjectsRecommendedList,
              projectList,
              type: 'workcations',
              showFilter: true,
            },
          },
        ]
      : []),
    ...(!isEmpty(announcements)
      ? [
          {
            component: ProjectAnnouncementsSection,
            data: {
              announcements,
              onGetInTouchClick: setIsModalOpen,
              checkForCTAButton: true,
            },
            sectionId: 'announcements',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Workcations',
                breadcrumbLink: RouteNames.workcations,
              },
              {
                breadcrumbName: 'Announcements',
                breadcrumbLink: '#',
              },
            ],
            theme: 'dark-theme',
            sectionName: 'announcement-section',
          },
        ]
      : []),
    ...(!isEmpty(completedProjects)
      ? [
          {
            component: CompletedProjectsSection,
            data: { 
              completedProjects, 
              onGetInTouchClick,
              sectionHeading: 'Completed Projects' },
            theme: 'light-theme',
            sectionId: 'completed-projects',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Workcations',
                breadcrumbLink: RouteNames.workcations,
              },
              {
                breadcrumbName: 'Completed Projects',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(awardData)
      ? [
          {
            component: Award,
            sectionId: 'awards',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Workcations',
                breadcrumbLink: RouteNames.workcations,
              },
              {
                breadcrumbName: 'Awards',
                breadcrumbLink: '#',
              },
            ],
            theme: 'dark-theme',
            data: awardData,
          },
        ]
      : []),
    ...(!isEmpty(videosData)
      ? [
          {
            component: Testimonial,
            sectionId: 'videos',
            theme: 'dark-theme',
            data: { testimonialData: videosData, type: 'videos' },
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Workcations',
                breadcrumbLink: RouteNames.workcations,
              },
              {
                breadcrumbName: 'Videos',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    {
      component: ContactUsSection,
      data: {
        contact,
        verticals,
        verticalId,
        callPostLeadAPI: true,
        type: 'no-padding',
      },
      location: props,
      sectionId: 'contact',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Workcations',
          breadcrumbLink: RouteNames.workcations,
        },
        {
          breadcrumbName: 'Contact Us',
          breadcrumbLink: '#',
        },
      ],
      theme: 'light-theme',
    },
    {
      component: FooterSection,
      theme: 'dark-theme',
      sectionId: 'te-links',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Workcations',
          breadcrumbLink: RouteNames.workcations,
        },
        {
          breadcrumbName: 'Links',
          breadcrumbLink: '#',
        },
      ],
      data: { siteSettings, footerLink },
    },
  ];

  return (
    <>
      <RenderSEO metaTags={metaTagSEO} currentRouteName={'workcations'} />
      <PageTemplate
        theme={theme}
        componentList={componentList}
        updateTheme={e => setTheme(e)}
        verticals={verticals}
        siteSettings={siteSettings}
        campaignCode={CampaignCode}
        salesEmail={contact.email}
        headerButtons={
          <HeaderButtons
            shareTheme={'dark'}
            getInTouchTheme={'light'}
            setIsModalOpen={setIsModalOpen}
            setIsShareModalOpen={setIsShareModalOpen}
          />
        }
      />
      <GetInTouchModal
        isOpen={isModalOpen}
        onClosePress={closeModal}
        campaignCode={CampaignCode}
        salesEmail={contact.email}
        callPostLeadAPI={true}
        verticalId={verticalId}
      />
      <ShareModal isOpen={isShareModalOpen} onClosePress={closeShareModal} />
    </>
  );
};

const mapStateToProps = (state: AppState, props: any) => {
  const verticalKey = NavigationUtil.getVerticalKeyForLocation(props.location);

  return {
    isLoading:
      HomeSelector.isLoading(state) ||
      AwardSelector.isLoading(state) ||
      FooterSelector.isLoading(state) ||
      CmsPagesSelector.isCmsPageLoading(state) ||
      TestimonialSelector.getTestimonialLoading(state) ||
      ProjectSelector.getProjectLoading(state) ||
      ProjectSelector.getAnnouncementsLoading(state) ||
      CmsDetailsSelector.getCmsDetailsDataLoading(state) ||
      ProductBrowserSelector.getProductBrowserLoading(state) ||
      VideosSelector.isLoading(state) ||
      ProjectSelector.getCompletedProjectsLoading(state),
    contact: HomeSelector.getContactForVertical(state, verticalKey),
    verticalId: HomeSelector.getVerticalIdForKey(verticalKey),
    verticals: HomeSelector.getVerticals(state),
    siteSettings: HomeSelector.getSiteSettings(state),
    bannerData: HomeSelector.getBanners(state, true, 'workcations', ''),
    announcements: ProjectSelector.getAnnouncements(state),
    ourDifferenceData: CmsPagesSelector.getOurDifferenceSection(
      state,
      'our-difference',
      'Workcations',
      HomeSelector.getVerticalIdForKey(verticalKey)
    ),
    awardData: AwardSelector.getAwardData(state),
    footerLink: FooterSelector.getFooterData(state),
    cmsDetailsData: CmsDetailsSelector.getCmsDetailsData(state),
    completedProjects: ProjectSelector.getCompletedProjects(state),
    allProjectsRecommendedList: ProjectBrowserSelector.getAllPropertiesRecommended(
      state
    ),
    projectList: ProjectSelector.getProjects(state),
    videosData: VideosSelector.getVideosData(state),
  };
};

const mapDispatchToProps = dispatch => {
  const {
    getContactDetails,
    getVerticals,
    getSiteSettings,
    getBannerData,
  } = HomeActions;
  const {
    getAnnouncements,
    getProjects,
    getCompletedProjects,
  } = ProjectActions;
  const { getPageData } = CmsPagesActions;
  const { getAwardData } = AwardActions;
  const { getFooterLinkData } = FooterActions;
  const { getCmsDetailsData } = CmsDetailsActions;
  const { getVideosData } = VideoActions;
  return bindActionCreators(
    {
      getContactDetails,
      getVerticals,
      getSiteSettings,
      getBannerData,
      getAnnouncements,
      getPageData,
      getAwardData,
      getCmsDetailsData,
      getCompletedProjects,
      getProjects,
      getVideosData,
      getFooterLinkData,
    },
    dispatch
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(WorkspacesPage);
